import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button, Card, CardHeader, DataTable, EmptyState } from "Components/ui";
import { CircleStop, FilePlus2, Pause, Pencil, Play, Plus, Repeat } from "Components/ui/icons";
import { Invoice, Schedule, TenantBilling } from "api/billing";
import { formatDate, formatINR, pluralize } from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import { ScheduleStatusChip } from "Routes/Billing/Components/StatusChips";
import { useInvoiceList, useScheduleActions } from "../data";
import { BillingPermissions } from "../types";

const DRAFTS_SHOWN = 50;

interface ChargesTabProps {
  tenantId: string;
  billing: TenantBilling;
  permissions: BillingPermissions;
  onAddSchedule: () => void;
  onEditSchedule: (schedule: Schedule) => void;
  onAddInvoice: () => void;
  onOpenInvoice: (invoiceId: string) => void;
}

type ScheduleDialog = { action: "pause" | "end"; schedule: Schedule } | null;

const ScheduleRow: React.FC<{
  schedule: Schedule;
  canWrite: boolean;
  busy: boolean;
  onEdit: () => void;
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
}> = ({ schedule, canWrite, busy, onEdit, onPause, onResume, onEnd }) => {
  const next = schedule.nextPeriod;
  const later = schedule.upcoming.slice(1);
  const openPause = schedule.pauses.filter((window) => !window.to).pop();
  const pausedSince = openPause ? openPause.from : null;
  return (
    <li className="px-5 py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-foreground">{schedule.name}</p>
            <ScheduleStatusChip status={schedule.status} />
          </div>
          <p className="mt-1 text-sm text-foreground">{schedule.planSummary}</p>
          {schedule.status === "paused" && (
            <p className="mt-1 text-sm text-warning-text">
              Paused{pausedSince ? ` since ${formatDate(pausedSince)}` : ""}. Periods whose invoice
              date falls in the pause are skipped; resuming bills from the next period.
            </p>
          )}
          {schedule.status === "active" && (
            <p className="mt-1 text-sm text-muted-foreground">
              {next ? (
                <>
                  Next:{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatINR(next.total)}
                  </span>{" "}
                  due {formatDate(next.dueDate)}
                  <span> · invoice goes out {formatDate(next.issueDate)}</span>
                </>
              ) : (
                "No more dues before the end date."
              )}
            </p>
          )}
          {later.length > 0 && schedule.status === "active" && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Then {later.map((period) => formatDate(period.dueDate)).join(", ")}
            </p>
          )}
          <p className="mt-1 text-xs text-subtle-foreground">
            From {formatDate(schedule.firstDueDate)}
            {schedule.endDate ? ` · ends ${formatDate(schedule.endDate)}` : " · no end date"} ·
            issued {pluralize(schedule.issueDaysBefore, "day")} before due
            {schedule.status === "ended" && schedule.endedAt
              ? ` · ended ${formatDate(schedule.endedAt)}`
              : ""}
          </p>
        </div>
        {canWrite && schedule.status !== "ended" && (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onEdit}
              disabled={busy}
              leftIcon={<Pencil aria-hidden="true" />}
            >
              Edit
            </Button>
            {schedule.status === "active" ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={onPause}
                disabled={busy}
                leftIcon={<Pause aria-hidden="true" />}
              >
                Pause
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={onResume}
                loading={busy}
                leftIcon={<Play aria-hidden="true" />}
              >
                Resume
              </Button>
            )}
            <Button
              variant="danger-outline"
              size="sm"
              onClick={onEnd}
              disabled={busy}
              leftIcon={<CircleStop aria-hidden="true" />}
            >
              End
            </Button>
          </div>
        )}
      </div>
    </li>
  );
};

const draftColumns: ColumnDef<Invoice>[] = [
  {
    id: "title",
    header: "Draft",
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.title || "Untitled draft"}</span>
    ),
  },
  {
    id: "due",
    header: "Due",
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDate(row.original.dueDate)}
      </span>
    ),
  },
  {
    id: "total",
    header: "Total",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-semibold tabular-nums text-foreground">
        {formatINR(row.original.total)}
      </span>
    ),
  },
];

/** Recurring charges (schedules) plus entry points for one-time invoices. */
const ChargesTab: React.FC<ChargesTabProps> = ({
  tenantId,
  billing,
  permissions,
  onAddSchedule,
  onEditSchedule,
  onAddInvoice,
  onOpenInvoice,
}) => {
  const actions = useScheduleActions(tenantId);
  const [dialog, setDialog] = React.useState<ScheduleDialog>(null);
  const [showEnded, setShowEnded] = React.useState<boolean>(false);
  const live = billing.schedules.filter((schedule) => schedule.status !== "ended");
  const ended = billing.schedules.filter((schedule) => schedule.status === "ended");
  // Drafts come from the invoice list, not the page's latest-20 invoices, so none is ever hidden.
  const draftList = useInvoiceList({
    tenantId,
    status: ["draft"],
    page: 1,
    pageSize: DRAFTS_SHOWN,
  });
  const drafts = draftList.data ? draftList.data.data : [];
  const moreDrafts = draftList.data ? draftList.data.total - drafts.length : 0;
  const busyId =
    actions.changeStatus.isLoading && actions.changeStatus.variables
      ? actions.changeStatus.variables.scheduleId
      : null;

  const headerActions = permissions.canWrite ? (
    <>
      <Button size="sm" onClick={onAddSchedule} leftIcon={<Plus aria-hidden="true" />}>
        Add recurring charge
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={onAddInvoice}
        leftIcon={<FilePlus2 aria-hidden="true" />}
      >
        One-time invoice
      </Button>
    </>
  ) : null;

  const renderRow = (schedule: Schedule) => (
    <ScheduleRow
      key={schedule._id}
      schedule={schedule}
      canWrite={permissions.canWrite}
      busy={busyId === schedule._id}
      onEdit={() => onEditSchedule(schedule)}
      onPause={() => setDialog({ action: "pause", schedule })}
      onResume={() => actions.changeStatus.mutate({ scheduleId: schedule._id, action: "resume" })}
      onEnd={() => setDialog({ action: "end", schedule })}
    />
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Recurring charges"
          description="SAP issues each invoice ahead of its due date; edits only affect periods not yet invoiced."
          actions={headerActions}
        />
        {live.length === 0 ? (
          <EmptyState
            compact
            icon={<Repeat aria-hidden="true" />}
            title="No recurring charges"
            description="Add the client's subscription (monthly, quarterly, yearly or custom). Use a one-time invoice for opening balances or advance payments."
          />
        ) : (
          <ul className="mt-4 divide-y divide-border border-t border-border">
            {live.map(renderRow)}
          </ul>
        )}
        {ended.length > 0 && (
          <div className="border-t border-border px-5 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowEnded((value) => !value)}
              aria-expanded={showEnded}
              className="-ml-3"
            >
              {showEnded ? "Hide" : "Show"} ended ({ended.length})
            </Button>
          </div>
        )}
        {showEnded && ended.length > 0 && (
          <ul className="divide-y divide-border rounded-b-card border-t border-border bg-surface-muted/50">
            {ended.map(renderRow)}
          </ul>
        )}
      </Card>

      {drafts.length > 0 && (
        <section className="space-y-3">
          <CardHeader
            className="p-0"
            title="Draft invoices"
            description={
              moreDrafts > 0
                ? `Not sent yet. Showing the newest ${drafts.length}; the Invoices tab lists all ${draftList.data?.total}.`
                : "Not sent yet. Open one to review and issue it."
            }
          />
          <DataTable
            columns={draftColumns}
            data={drafts}
            density="compact"
            enableSorting={false}
            caption="Draft invoices"
            getRowId={(invoice) => invoice._id}
            onRowClick={(invoice) => onOpenInvoice(invoice._id)}
          />
        </section>
      )}

      {dialog && dialog.action === "pause" && (
        <ConfirmDialog
          title={`Pause “${dialog.schedule.name}”?`}
          description="No invoices are issued while it is paused. Periods due while it is paused are skipped for good (no back-billing); periods due after you resume are billed then."
          confirmLabel="Pause charge"
          onConfirm={() =>
            actions.changeStatus.mutateAsync({ scheduleId: dialog.schedule._id, action: "pause" })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog && dialog.action === "end" && (
        <ConfirmDialog
          title={`End “${dialog.schedule.name}”?`}
          description="No further invoices are created. Invoices already issued stay as they are. This can't be undone; add a new charge to bill again."
          confirmLabel="End charge"
          tone="danger"
          onConfirm={() =>
            actions.changeStatus.mutateAsync({ scheduleId: dialog.schedule._id, action: "end" })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default ChargesTab;
